// The ship's own position, and the bubble it fights in.
//
// The authority is `simPosition`: a heliocentric ecliptic J2000 vector in
// metres, held in doubles off the three.js transform, per
// `docs/roadmap/01-world-model.md`. float32 carries a 24-bit mantissa, so a
// heliocentric metre at Mars's own distance quantises to steps of about 16 km,
// and nothing the player sees is ever drawn from that number directly.
//
// The combat bubble is not a place. It is ANCHORED at `anchor` - the ship's own
// position snapped to a 1,024 m grid - so it exists wherever the ship is and
// follows it, and nothing in code or in a scenario carries a bubble location.
// Two frames come off the doubles, both derived every frame and neither ever
// written by hand:
//
//   scene   `world - anchor`, the metres the scene is drawn in. The anchor is a
//           power-of-two grid point, so the scene coordinate stays inside one
//           quantum of the origin however far the ship has travelled and a
//           rebase changes no low bit of a local float32 position.
//   bubble  `basis^-1 (world - seed)`, the fight's own local metres. `seed` is
//           where the ship WAS when the fight was seeded, carried since by its
//           frame parent's travel, so the ship starts at the fight's own zero
//           and every scripted position in a scenario is ship-relative by
//           construction; `basis` is the ship's own orbital frame, so the fight
//           happens on the plane the ship is already flying in rather than on
//           world Y.
//
// The bubble group therefore carries `seed - anchor` and the whole fight
// translates rigidly with the scene origin on a rebase, which is what keeps the
// frame the player is looking at identical across one.
//
// Inside a fight the ship's LOCAL metres are held as their own doubles and
// `world` is written from them, not the other way round. Read back out of the
// heliocentric doubles, a local position carries the rounding of a number
// hundreds of millions of kilometres long, which depends on where in the solar
// system the fight is and on how many drawn frames `carry()` re-rounded it on -
// a seed replayed on two loads then drifts apart by a few nanometres a step,
// and the captains amplify that into a different fight. `setLocal`, `seedAt` and
// `advanceLocal` hold the local metres; `advance` and `holdAt`, which write
// the heliocentric point itself, hand the authority back to `world`.

import type { WorldVec } from '../world/frame';

/** Metres. A power of two, so a rebase is exact in float32. */
export const REBASE_QUANTUM_M = 1024;

/** Anything carrying the three components; `THREE.Vector3` satisfies it. */
export interface Xyz {
  x: number;
  y: number;
  z: number;
}

/** An orthonormal frame, each axis a unit vector in world (ecliptic) metres. */
export interface Basis {
  readonly x: WorldVec;
  readonly y: WorldVec;
  readonly z: WorldVec;
}

export interface SimPositionHandle {
  /** Heliocentric ecliptic J2000 metres, doubles. The authority. */
  readonly world: WorldVec;
  /** The bubble's own origin: the ship's position, snapped to the quantum. */
  readonly anchor: WorldVec;
  /** Where the ship was when the fight was seeded. The fight's local zero. */
  readonly seed: WorldVec;
  /** The fight's own axes: +y is the ship's orbital normal, +x along-track. */
  readonly basis: Basis;
  readonly quantum: number;
  /** How many times the anchor has moved. */
  readonly rebases: number;
  /** Add a step in WORLD metres to the authoritative doubles. */
  advance(dx: number, dy: number, dz: number): void;
  /**
   * Add a step in the bubble's own LOCAL metres to the held local position and
   * put `world` where it lands: the fight's step, whose outcome must not depend
   * on the heliocentric point it happens at.
   */
  advanceLocal(dx: number, dy: number, dz: number): void;
  /**
   * Move the ship and the bubble's zero together by a step in WORLD metres:
   * the whole fight goes with its parent and every local metre in it stays.
   */
  carry(dx: number, dy: number, dz: number): void;
  /**
   * Set the authoritative doubles to an absolute point, with no step summed
   * in. For a place that is re-derived from a pin every tick rather than
   * integrated, so N ticks of that re-derivation never compound float noise
   * the way N calls to `advance()` would.
   */
  holdAt(at: WorldVec): void;
  /** Put the ship at a heliocentric point and seed the fight around it. */
  seedAt(world: WorldVec, basis: Basis): void;
  /**
   * Put back a frame a save recorded: the ship's point, the bubble's own zero
   * and its axes, each as they were. Unlike `seedAt` the seed is given rather
   * than taken from the ship, because a ship that has travelled since the
   * bubble was seeded is not at its zero and every seeded bearing in the
   * fight is measured from that zero.
   */
  restoreFrame(world: WorldVec, seed: WorldVec, basis: Basis): void;
  /** Put the ship at a point in the bubble's own local metres, and hold it there. */
  setLocal(p: Xyz): void;
  /** The ship in the bubble's own local metres: the held position while one is
   *  held, else `basis^-1 (world - seed)`. */
  localPosition<T extends Xyz>(out: T): T;
  /** `seed - anchor`: where the bubble group sits in the scene. */
  sceneOffset<T extends Xyz>(out: T): T;
  /** A bubble-local vector in world axes. */
  rotate<T extends Xyz>(local: Xyz, out: T): T;
  /** A world-axes vector in the bubble's own. */
  unrotate<T extends Xyz>(world: Xyz, out: T): T;
  /** The camera's own world position, from where it sits in the scene. */
  cameraWorld<T extends Xyz>(sceneCamera: Xyz, out: T): T;
  /**
   * Move the anchor if the camera has left the quantum, and report the shift
   * in scene metres so the bubble can follow it. Null when nothing moved.
   */
  rebase(sceneCamera: Xyz): WorldVec | null;
}

export interface SimPositionConfig {
  /** The ship's position at boot, heliocentric ecliptic J2000 metres. */
  start: WorldVec;
  /** The fight's axes at boot. */
  basis: Basis;
  /** Metres, a power of two. Defaults to `REBASE_QUANTUM_M`. */
  quantum?: number;
}

function snap(v: number, quantum: number): number {
  return Math.round(v / quantum) * quantum;
}

function copyInto(dst: WorldVec, src: Xyz): void {
  dst.x = src.x;
  dst.y = src.y;
  dst.z = src.z;
}

/**
 * The fight's own axes from a heliocentric state: +y the orbital normal
 * `r x v`, +x the along-track direction, +z completing a right-handed set.
 *
 * OWNER DECISION 1 keeps combat on one plane; this is which plane. A fight
 * outside Pluto happens on Pluto's own orbital plane and one in Mars orbit on
 * Mars's, with no second code path and no teleport to either.
 */
export function orbitalBasis(r: Xyz, v: Xyz): Basis {
  const nx = r.y * v.z - r.z * v.y;
  const ny = r.z * v.x - r.x * v.z;
  const nz = r.x * v.y - r.y * v.x;
  if (!(Math.hypot(nx, ny, nz) > 0)) {
    throw new Error('orbitalBasis: r x v is degenerate, so the state has no plane');
  }
  return basisFromNormal({ x: nx, y: ny, z: nz }, v);
}

/**
 * The same axes from a plane that is handed in rather than solved from the
 * state: +y the normal, +x `along` with the normal taken out of it, +z
 * completing the set.
 *
 * A state whose position and velocity are parallel names no plane of its own -
 * `r x v` is zero in exact arithmetic there and float noise in doubles - so a
 * caller that seeds one, such as a park set moving straight out from a body,
 * has to say which plane the fight is on. The body's own orbital plane is the
 * answer that keeps OWNER DECISION 1 above.
 */
export function basisFromNormal(normal: Xyz, along: Xyz): Basis {
  const nl = Math.hypot(normal.x, normal.y, normal.z);
  if (!(nl > 0)) throw new Error('basisFromNormal: the normal has no length, so there is no plane');
  const y: WorldVec = { x: normal.x / nl, y: normal.y / nl, z: normal.z / nl };
  const vn = along.x * y.x + along.y * y.y + along.z * y.z;
  const ax = along.x - y.x * vn;
  const ay = along.y - y.y * vn;
  const az = along.z - y.z * vn;
  const al = Math.hypot(ax, ay, az);
  if (!(al > 0)) {
    throw new Error('basisFromNormal: the along-track direction is parallel to the normal');
  }
  const x: WorldVec = { x: ax / al, y: ay / al, z: az / al };
  const z: WorldVec = {
    x: x.y * y.z - x.z * y.y,
    y: x.z * y.x - x.x * y.z,
    z: x.x * y.y - x.y * y.x,
  };
  return { x, y, z };
}

export function createSimPosition(config: SimPositionConfig): SimPositionHandle {
  const quantum = config.quantum ?? REBASE_QUANTUM_M;
  const world: WorldVec = { x: config.start.x, y: config.start.y, z: config.start.z };
  const seed: WorldVec = { x: world.x, y: world.y, z: world.z };
  const anchor: WorldVec = {
    x: snap(world.x, quantum),
    y: snap(world.y, quantum),
    z: snap(world.z, quantum),
  };
  const bx: WorldVec = { ...config.basis.x };
  const by: WorldVec = { ...config.basis.y };
  const bz: WorldVec = { ...config.basis.z };
  const basis: Basis = { x: bx, y: by, z: bz };
  let rebases = 0;
  /** The ship in local metres, the authority while `localHeld`; `world` is then
   *  `seed + basis local`, rewritten whenever either side of that moves. */
  const local: WorldVec = { x: 0, y: 0, z: 0 };
  let localHeld = true;

  function deriveLocal(out: Xyz): void {
    const dx = world.x - seed.x;
    const dy = world.y - seed.y;
    const dz = world.z - seed.z;
    out.x = dx * bx.x + dy * bx.y + dz * bx.z;
    out.y = dx * by.x + dy * by.y + dz * by.z;
    out.z = dx * bz.x + dy * bz.y + dz * bz.z;
  }

  function placeFromLocal(): void {
    world.x = seed.x + bx.x * local.x + by.x * local.y + bz.x * local.z;
    world.y = seed.y + bx.y * local.x + by.y * local.y + bz.y * local.z;
    world.z = seed.z + bx.z * local.x + by.z * local.y + bz.z * local.z;
  }

  return {
    world,
    anchor,
    seed,
    basis,
    quantum,
    get rebases(): number { return rebases; },
    advance(dx: number, dy: number, dz: number): void {
      world.x += dx;
      world.y += dy;
      world.z += dz;
      localHeld = false;
    },
    advanceLocal(dx: number, dy: number, dz: number): void {
      if (!localHeld) {
        deriveLocal(local);
        localHeld = true;
      }
      local.x += dx;
      local.y += dy;
      local.z += dz;
      placeFromLocal();
    },
    carry(dx: number, dy: number, dz: number): void {
      if (localHeld) {
        seed.x += dx;
        seed.y += dy;
        seed.z += dz;
        placeFromLocal();
        return;
      }
      // The offset is taken before the zero moves and put back on it, so the
      // local metres pick up one rounding rather than one on each end.
      const lx = world.x - seed.x;
      const ly = world.y - seed.y;
      const lz = world.z - seed.z;
      seed.x += dx;
      seed.y += dy;
      seed.z += dz;
      world.x = seed.x + lx;
      world.y = seed.y + ly;
      world.z = seed.z + lz;
    },
    holdAt(at: WorldVec): void {
      copyInto(world, at);
      localHeld = false;
    },
    seedAt(at: WorldVec, next: Basis): void {
      copyInto(world, at);
      copyInto(seed, at);
      local.x = 0;
      local.y = 0;
      local.z = 0;
      localHeld = true;
      copyInto(bx, next.x);
      copyInto(by, next.y);
      copyInto(bz, next.z);
      anchor.x = snap(world.x, quantum);
      anchor.y = snap(world.y, quantum);
      anchor.z = snap(world.z, quantum);
    },
    restoreFrame(at: WorldVec, seededAt: WorldVec, next: Basis): void {
      copyInto(world, at);
      copyInto(seed, seededAt);
      localHeld = false;
      copyInto(bx, next.x);
      copyInto(by, next.y);
      copyInto(bz, next.z);
      anchor.x = snap(world.x, quantum);
      anchor.y = snap(world.y, quantum);
      anchor.z = snap(world.z, quantum);
    },
    setLocal(p: Xyz): void {
      local.x = p.x;
      local.y = p.y;
      local.z = p.z;
      localHeld = true;
      placeFromLocal();
    },
    localPosition<T extends Xyz>(out: T): T {
      if (localHeld) {
        out.x = local.x;
        out.y = local.y;
        out.z = local.z;
      } else {
        deriveLocal(out);
      }
      return out;
    },
    sceneOffset<T extends Xyz>(out: T): T {
      out.x = seed.x - anchor.x;
      out.y = seed.y - anchor.y;
      out.z = seed.z - anchor.z;
      return out;
    },
    rotate<T extends Xyz>(local: Xyz, out: T): T {
      const { x, y, z } = local;
      out.x = bx.x * x + by.x * y + bz.x * z;
      out.y = bx.y * x + by.y * y + bz.y * z;
      out.z = bx.z * x + by.z * y + bz.z * z;
      return out;
    },
    unrotate<T extends Xyz>(w: Xyz, out: T): T {
      const { x, y, z } = w;
      out.x = x * bx.x + y * bx.y + z * bx.z;
      out.y = x * by.x + y * by.y + z * by.z;
      out.z = x * bz.x + y * bz.y + z * bz.z;
      return out;
    },
    cameraWorld<T extends Xyz>(sceneCamera: Xyz, out: T): T {
      out.x = anchor.x + sceneCamera.x;
      out.y = anchor.y + sceneCamera.y;
      out.z = anchor.z + sceneCamera.z;
      return out;
    },
    rebase(sceneCamera: Xyz): WorldVec | null {
      const { x, y, z } = sceneCamera;
      const sx = world.x - anchor.x;
      const sy = world.y - anchor.y;
      const sz = world.z - anchor.z;
      const q2 = quantum * quantum;
      // Either the camera or the ship leaving the quantum moves the anchor:
      // the bar the bubble is held to is a distance from the SHIP, and a chase
      // camera trails it by tens of metres.
      if ((x * x) + (y * y) + (z * z) <= q2 && (sx * sx) + (sy * sy) + (sz * sz) <= q2) return null;
      const nx = snap(world.x, quantum);
      const ny = snap(world.y, quantum);
      const nz = snap(world.z, quantum);
      const dx = nx - anchor.x;
      const dy = ny - anchor.y;
      const dz = nz - anchor.z;
      if (dx === 0 && dy === 0 && dz === 0) return null;
      anchor.x = nx;
      anchor.y = ny;
      anchor.z = nz;
      rebases++;
      return { x: dx, y: dy, z: dz };
    },
  };
}
